import { Empty, Input, Select, Typography } from "antd";
import React from "react";
import ReactHTMLParser from "react-html-parser";
import {
  InteractionNotificationPreview,
  Template,
} from "../helpers/shared-interfaces";
import { filterMethod } from "../helpers/utilities";
import ModalCreator from "./ModalCreator";

const { Text, Title } = Typography;

interface Props {
  showModal: boolean;
  templates: Template[];
  selectedTemplateId?: string;
  comment: string;
  requiresComment: boolean;
  preview?: InteractionNotificationPreview | null;
  isLoading: boolean;
  isSending: boolean;
  onTemplateChange: (templateId: string) => void;
  onCommentChange: (comment: string) => void;
  onSend: () => void;
  onClose: () => void;
}

export default function DelayUpdateModal(props: Props): JSX.Element {
  const { preview } = props;
  const isCommentMissing = props.requiresComment && props.comment.trim() === "";

  return ModalCreator({
    title: "Send Delay Update",
    okType: "primary",
    okText: "Send",
    showModal: props.showModal,
    isLoading: props.isLoading || props.isSending,
    onOkay: props.onSend,
    toggleModal: props.onClose,
    okayButtonProps: {
      disabled:
        !props.selectedTemplateId ||
        !preview ||
        isCommentMissing ||
        props.isLoading ||
        props.isSending,
    },
    width: 760,
    inscription: (
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <Text strong>Template</Text>
          <Select
            showSearch
            value={props.selectedTemplateId}
            style={{ width: "100%", marginTop: 8 }}
            optionFilterProp="children"
            filterOption={filterMethod}
            placeholder="Select a delay notification template"
            onChange={props.onTemplateChange}
          >
            {props.templates.map((template) => (
              <Select.Option key={template.id} value={template.id}>
                {template.name}
              </Select.Option>
            ))}
          </Select>
        </div>

        {props.requiresComment && (
          <div>
            <Text strong={true}>Custom Comment</Text>
            <Text type="danger" style={{ marginLeft: 4 }}>
              *
            </Text>
            <Input.TextArea
              value={props.comment}
              rows={4}
              style={{ marginTop: 8 }}
              placeholder="Add a custom comment to include in the template"
              onChange={(event): void =>
                props.onCommentChange(event.target.value)
              }
            />
          </div>
        )}

        {!preview && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a template to preview the delay update."
          />
        )}

        {preview && (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "grid", gap: 4 }}>
              <Text strong>Recipient</Text>
              <Text>{preview.recipient}</Text>
            </div>
            {preview.ccRecipients && preview.ccRecipients.length > 0 && (
              <div style={{ display: "grid", gap: 4 }}>
                <Text strong>CC</Text>
                <Text>{preview.ccRecipients.join(", ")}</Text>
              </div>
            )}
            <div style={{ display: "grid", gap: 4 }}>
              <Text strong>Channel</Text>
              <Text>{preview.notificationType.toUpperCase()}</Text>
            </div>
            {preview.subject && (
              <div style={{ display: "grid", gap: 4 }}>
                <Text strong>Subject</Text>
                <Text>{preview.subject}</Text>
              </div>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              <Title level={5} style={{ margin: 0 }}>
                Preview
              </Title>
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: 16,
                  background: "#fafafa",
                  whiteSpace: "pre-wrap",
                }}
              >
                {preview.templateType === "email"
                  ? ReactHTMLParser(preview.body)
                  : preview.body}
              </div>
            </div>
          </div>
        )}
      </div>
    ),
  });
}
